/* ============================================================
   EFX Team Order — multi-jersey builder for Itoris DPO products
   ============================================================
   Lets a customer configure N fully-customized jerseys of one
   product and add them all to the cart as N separate line items,
   each with its own server-issued DPO configuration variant.

   HOW IT WORKS (and why it works this way)
   ----------------------------------------
   DPO is a single-instance app: window.dpoOptions is one object
   hard-bound to the singleton DOM id #itoris_dynamicproductoptions,
   and the 6,400-line EFX customization layer queries
   [id^="dynamic_option_id_"] globally. Cloning the customizer per
   row would make DPO's rules, visibility engine and pricing
   cross-talk between rows.

   So there is exactly ONE live customizer. The rows are an
   accordion; expanding a row MOVES the real .dpo-container into
   that row and replays the row's saved answers into it. Collapsing
   snapshots them back out. Every EFX phase and every DPO rule keeps
   working, untouched, because it is always the same DOM.

   On "Add all to cart" each row is replayed through DPO in turn and
   put through DPO's own validation and its own ValidateForm round
   trip — so every row gets its own real, server-issued (D#…)
   configuration variant. No config id is ever reused across rows.

   This file never touches the cart template, checkout, or the
   normal single-item add-to-cart path.
   ============================================================ */

(function () {
  'use strict';

  var CFG = window.EFX_TEAM_ORDER;
  if (!CFG) return;

  // Garment noun, identified from the product's tag in Liquid, so the UI reads
  // "Skort 1", "5 polos", etc. instead of a generic word. NOUN = capitalized
  // singular (row labels, sentence starts); NOUN_LC = lowercase singular
  // (mid-sentence); NOUN_PL = lowercase plural (counts). Defaults keep the
  // builder working if the snippet didn't supply them.
  var NOUN = CFG.itemNoun || 'Item';
  var NOUN_PL = CFG.itemNounPlural || 'items';
  var NOUN_LC = NOUN.toLowerCase();
  function nCount(n) { return n === 1 ? NOUN_LC : NOUN_PL; } // "1 skort" / "5 skorts"

  // Bump this every push so the preview can be confirmed as up to date. It is
  // logged on load and shown in the drawer header.
  var BUILD = 'build 2026-07-15 · T (product-identified garment noun)';
  try { console.log('[EFX Team Order] ' + BUILD); } catch (e) { /* noop */ }

  var DPO_ROOT = 'itoris_dynamicproductoptions';
  var CONTAINER = '.dpo-container';
  var SETTLE_MS = 350; // let DPO + EFX rule engines finish cascading

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /** Parse the merchant's bulk-discount setting into sorted tiers.
   *  Accepts "5:5, 10:10, 20:15" or newline-separated "qty:percent" lines.
   *  Returns [{min, pct}] sorted by min ascending. Malformed entries are
   *  skipped rather than throwing, so a typo can't break the drawer. */
  var TIERS = (function () {
    var raw = (CFG.bulkTiers || '').trim();
    if (!raw) return [];
    return raw.split(/[\n,]+/).map(function (part) {
      var m = part.trim().match(/^(\d+)\s*[:=]\s*(\d+(?:\.\d+)?)/);
      if (!m) return null;
      var min = parseInt(m[1], 10);
      var pct = parseFloat(m[2]);
      if (!min || min < 1 || !(pct > 0) || pct >= 100) return null;
      return { min: min, pct: pct };
    }).filter(Boolean).sort(function (a, b) { return a.min - b.min; });
  })();

  /** Highest tier whose min is met by the total jersey quantity. */
  function tierFor(qty) {
    var hit = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (qty >= TIERS[i].min) hit = TIERS[i];
    }
    return hit;
  }

  var state = {
    rows: [],
    active: null,        // index of the expanded row, or null
    seq: 0,              // row id counter
    defaultSnap: null,   // DPO's pristine state, captured before we touch anything
    pageSnap: null,      // what the shopper had on the page when they opened the drawer
    pageQty: null,
    home: null,          // anchor marking where .dpo-container lives on the page
    open: false,
    busy: false
  };

  // Diagnostic: dump EVERY row's stored summary + saved size snapshot.
  window.__efxTOSnap = function () {
    var out = {
      activeIndex: state.active,
      rows: state.rows.map(function (r, i) {
        var sizeKeys = {};
        if (r.snap) {
          Object.keys(r.snap.f).forEach(function (k) {
            if (/\[100[4-7]\]/.test(k)) sizeKeys[k] = r.snap.f[k];
          });
        }
        return { i: i, summary: r.summary, added: r.added, sizeKeys: sizeKeys };
      })
    };
    console.log('=== __efxTOSnap ===\n' + JSON.stringify(out, null, 2));
    return out;
  };

  /* ---------------------------------------------------------- */
  /* DPO plumbing                                               */
  /* ---------------------------------------------------------- */

  function dpoReady() {
    return !!(
      window.jQuery &&
      window.dpoObserver &&
      window.dpoOptions &&
      window.dpoOptions.options &&
      window.dpoOptions.options.length &&
      document.getElementById(DPO_ROOT) &&
      $(CONTAINER)
    );
  }

  /** Every named control DPO owns. */
  function controls() {
    var root = document.getElementById(DPO_ROOT);
    if (!root) return [];
    return $$('input, select, textarea', root).filter(function (el) { return !!el.name; });
  }

  /** Stable identity for a control across snapshot/restore. Radios and
   *  checkboxes share a name, so they need their value to disambiguate. */
  function keyOf(el) {
    var t = el.type;
    return el.name + '||' + (t === 'radio' || t === 'checkbox' ? el.value : '');
  }

  function snapshot() {
    var snap = { f: {}, files: {} };
    controls().forEach(function (el) {
      var k = keyOf(el);
      if (el.type === 'file') snap.files[k] = (el.files && el.files.length) ? el.files[0] : null;
      else if (el.type === 'radio' || el.type === 'checkbox') snap.f[k] = !!el.checked;
      else snap.f[k] = el.value;
    });
    return snap;
  }

  function cloneSnap(snap) {
    if (!snap) return null;
    var out = { f: {}, files: {} };
    Object.keys(snap.f).forEach(function (k) { out.f[k] = snap.f[k]; });
    // File objects are immutable — sharing the reference across rows is safe.
    Object.keys(snap.files).forEach(function (k) { out.files[k] = snap.files[k]; });
    return out;
  }

  /** DPO binds its handlers with jQuery .on(), which listens for native events —
   *  so ONE native dispatch is enough. Also calling jQuery().trigger() would run
   *  every handler a second time, and each one recomputes visibility across all
   *  39 options. With ~50 controls per restore and two restores per row, that
   *  doubling is felt as a main-thread stall. */
  function fire(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Can this browser assign a FileList programmatically? If not, we must not
   *  pretend rows carry their own uploads — see restore(). */
  var canSetFiles = (function () {
    try {
      var i = document.createElement('input');
      i.type = 'file';
      i.files = new DataTransfer().files;
      return true;
    } catch (e) {
      return false;
    }
  })();

  /** One pass: set each control to its snapshot value and fire it IMMEDIATELY,
   *  in DOM order. Interleaving set+fire (rather than set-all-then-fire-all) is
   *  essential: a field like "Size Type" governs the visibility/contents of a
   *  later field ("Men Sizes"). If we set the size first and only fire Size Type
   *  afterwards, DPO's cascade clears the size we just set. Firing each field
   *  before moving to the next lets the size be set AFTER its governing field
   *  has settled — exactly as a human filling the form top-to-bottom. */
  function applySnapOnce(snap) {
    controls().forEach(function (el) {
      var k = keyOf(el);

      if (el.type === 'file') {
        var want = (snap.files && snap.files[k]) || null;
        var has = (el.files && el.files.length) ? el.files[0] : null;
        if (has === want) return;
        // Clear FIRST to a known-empty state so a failed assignment never leaves
        // the previous row's artwork on this input.
        try {
          el.value = '';
          if (canSetFiles) el.files = new DataTransfer().files;
        } catch (e) { /* noop */ }
        if (want && canSetFiles) {
          try {
            var dt = new DataTransfer();
            dt.items.add(want);
            el.files = dt.files;
          } catch (e) { /* left empty — the row will fail validation, loudly */ }
        }
        fire(el);
        return;
      }

      if (!(k in snap.f)) return;
      var v = snap.f[k];

      if (el.type === 'radio' || el.type === 'checkbox') {
        if (el.checked !== v) { el.checked = v; fire(el); }
      } else if (el.value !== v) {
        el.value = v;
        fire(el);
      }
    });
  }

  var restoreSeq = 0;

  // Set true the instant the shopper physically interacts with the live
  // customizer after a restore, so the post-restore re-fill (refillCleared)
  // stops re-asserting the snapshot's OLD size over a fresh pick. That stale
  // re-assert was the "double-click on duplicated jerseys" bug: the first click
  // set the new size, DPO briefly blanked it, and the 450ms re-fill put the old
  // value back — so a second click was needed. A real pointer/key event is
  // trusted; DPO's own synthetic events are not, so restore() can't trip it.
  var restoreUserTouch = false;
  (function bindRestoreTouch() {
    function touch(e) {
      if (e.isTrusted && e.target && e.target.closest && e.target.closest(CONTAINER)) {
        restoreUserTouch = true;
      }
    }
    document.addEventListener('pointerdown', touch, true);
    document.addEventListener('keydown', touch, true);
  })();

  /** Replay a snapshot into the one live customizer.
   *
   *  Runs an immediate interleaved pass, then re-asserts a couple of times to
   *  beat DPO's async cascade (it clears/repopulates the size dropdowns keyed
   *  off Size Type AFTER we set them). The re-assert is cancelled the instant
   *  the shopper interacts — otherwise it would overwrite the size they just
   *  picked (which is exactly what "changing the size doesn't register" was).
   *
   *  Calls the EFX visibility engine (Phase 11) immediately after applying so a
   *  restored row reveals the right conditional field (e.g. the Women size
   *  dropdown under Size Type=Women) at once, instead of blank until that
   *  engine's own delayed pass runs. This is safe now that Phase 11 itself
   *  hides the custom-logo fields for PBA/Neon — earlier it re-showed them,
   *  which is why this call had been removed. */
  function applyVisibilityRules() {
    try { if (window.__efxApplyRules) window.__efxApplyRules(); } catch (e) { /* noop */ }
  }

  /** The size shown in the row header. Reads the currently VISIBLE size field
   *  (the selected Size Type), not the raw option map — a duplicated jersey can
   *  carry a stale hidden value from another type (e.g. a leftover "Men: Medium"
   *  next to the real "Women: Large"), and the map would surface whichever comes
   *  first. The visible field is always the true, selected size. */
  function visibleSizeText() {
    var root = document.getElementById(DPO_ROOT);
    if (!root) return '';
    // Read the ACTIVE size field's select directly. Phase 8c relocated the
    // .efx-size-pills grids out from under their size fields (they now live
    // under the Size Type cards), so we can no longer find the size field via
    // the pills. Instead we scan the size value fields themselves and read
    // whichever is currently active — its Size Type is checked, so DPO leaves
    // it un-.ihidden and visible. The inactive types (carrying a stale value
    // from a duplicated jersey, e.g. a leftover Men's Large next to the real
    // Youth 2T) are .ihidden and skipped.
    var fields = $$('[id^="dynamic_option_id_"]', root);
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (field.classList.contains('ihidden')) continue;
      if (getComputedStyle(field).display === 'none') continue;
      var lbl = field.querySelector('label');
      if (!lbl || !/\bsizes?\b/i.test(lbl.textContent || '')) continue; // a size field
      var sel = field.querySelector('select');
      if (sel && sel.value && sel.selectedIndex >= 0) {
        var opt = sel.options[sel.selectedIndex];
        if (opt) return opt.text.replace(/\s*\+\$.*$/, '').replace(/\s*--.*/, '').trim();
      }
    }
    return '';
  }

  function restore(snap) {
    if (!snap) return;
    var mySeq = ++restoreSeq;
    restoreUserTouch = false; // fresh restore — re-fill is allowed until the shopper acts

    // Signal to the size-pill proxy (efx-dpo-customizations.js) that a replay is
    // in progress, so its "re-assert the committed size if DPO blanks it" guard
    // stands down — otherwise it would fight the restore and bleed one jersey's
    // size into another. Cleared once this restore's re-fills are done, unless a
    // newer restore has taken over.
    window.__efxTORestoring = true;

    applySnapOnce(snap);
    applyVisibilityRules();

    // Targeted re-assert. DPO can asynchronously EMPTY a size dropdown shortly
    // after we set it (its Size Type cascade repopulates dependent selects). So
    // a moment later we re-fill ONLY select/text fields that went blank while
    // the snapshot had a value.
    //
    // Deliberately conservative — it must never fight the shopper:
    //   • skips radios/checkboxes entirely, so it can't revert a size-TYPE
    //     change (Men→Women) the shopper just made;
    //   • only fills BLANK fields, so it can't overwrite a size they changed to
    //     a different value.
    // (The old version re-applied the whole snapshot, which reverted type
    //  changes — that was the "second jersey trumps the first" bug.)
    function refillCleared() {
      if (mySeq !== restoreSeq) return; // a newer restore superseded this one
      if (restoreUserTouch) return;     // the shopper has started editing — don't fight them
      controls().forEach(function (el) {
        if (el.type === 'radio' || el.type === 'checkbox' || el.type === 'file') return;
        var k = keyOf(el);
        if (!(k in snap.f)) return;
        var want = snap.f[k];
        if (want && !el.value) { el.value = want; fire(el); }
      });
      applyVisibilityRules();
    }
    setTimeout(refillCleared, 150);
    setTimeout(refillCleared, 450);
    // Release the proxy-suppression flag once the re-fills are done, unless a
    // newer restore has since taken over.
    setTimeout(function () { if (mySeq === restoreSeq) window.__efxTORestoring = false; }, 600);
  }

  function settle(ms) {
    return new Promise(function (res) { setTimeout(res, ms == null ? SETTLE_MS : ms); });
  }

  /* The custom-logo hiding for PBA/PWBA (Phase 10) and Neon (Phase 13) lives in
     the shared EFX layer, so it applies to the builder automatically — no
     builder-side lock needed. (Phase 10 now leaves Neon to Phase 13, and Phase
     13 hides the fields outright rather than only greying them.) */

  /** Match DPO's own getQtyField(): the FIRST input[name="quantity"] in the
   *  document, not scoped to the theme's form. If the theme's quantity box is
   *  turned off in settings, DPO injects its own hidden qty input into
   *  .dpo-container and prices against exactly this element — a form-scoped
   *  lookup would miss it and every row would price at qty 1 while the cart got
   *  the real quantity. */
  function themeQtyInput() {
    return document.querySelector('input[name="quantity"]');
  }

  function setThemeQty(q) {
    var el = themeQtyInput();
    if (!el) {
      // Never silently no-op: with no qty field, DPO prices every row at 1.
      // Surface the assumption rather than shipping a wrong price quietly.
      if (window.console && console.warn) {
        console.warn('[efx-team-order] No input[name="quantity"] found; treating quantity as 1.');
      }
      return;
    }
    if (String(el.value) === String(q)) return;
    el.value = q;
    fire(el);
  }

  /** DPO's authoritative computed price for whatever is currently in the form. */
  function currentPrice() {
    try {
      var r = window.dpoOptions.updatePrice(true);
      var p = r && r.current_prices ? r.current_prices.price : null;
      return typeof p === 'number' && isFinite(p) ? p : null;
    } catch (e) {
      return null;
    }
  }

  /** A short human summary for the collapsed row header. */
  function currentSummary() {
    var map = {};
    try {
      (window.dpoOptions.getAllOptionValues(true) || []).forEach(function (v) {
        var t = String(v.title == null ? '' : v.title).replace(/:\s*$/, '').trim().toLowerCase();
        map[t] = String(v.value == null ? '' : v.value).trim();
      });
    } catch (e) { /* noop */ }

    // Row header reads: "<first line> <second line> - Size - Collar"
    // (both name lines shown, e.g. "SMITH 12"; falls back to "No name").
    var line1 = map['first line text'] || '';
    var line2 = map['second line text'] || '';
    var name = [line1, line2].filter(Boolean).join(' ');
    // Prefer the visible size field (correct selected-type size); fall back to
    // the option map for pants/shorts/skort, which use a single size dropdown.
    var size = visibleSizeText() ||
      map['skort size'] || map['shorts size'] || map['pants waist size'] ||
      map['men sizes'] || map['women sizes'] || map['youth sizes'] || '';
    var collar = map['collar style'] || '';

    var bits = [name || 'No name'];
    if (size) bits.push(size);
    if (collar) bits.push(collar);

    return bits.join(' - ');
  }

  /* ---------------------------------------------------------- */
  /* Server round trip — one config variant per row             */
  /* ---------------------------------------------------------- */

  /** Faithful port of DPO's own validateVariantConfig() payload harvest.
   *  Reads DPO's model (dpoOptions.options[].elm), so it stays correct as
   *  templates change. */
  function buildValidatePayload(qty, cartItems) {
    var jq = window.jQuery;
    var dpo = window.dpoOptions;
    var obs = window.dpoObserver;
    var options = { 'options[0]': 'dummy_option' };

    jq.each(dpo.options, function (i, o) {
      var choiceTypes = ['drop_down', 'radio', 'checkbox', 'multiple'];

      if (o.items && o.items.length && choiceTypes.indexOf(o.type) > -1) {
        if (o.elm && o.elm.disabled) return;
        jq.each(o.items, function (k, so) {
          if (!so.elm || so.elm.disabled) return;
          if (!so.elm.selected && !so.elm.checked) return;
          if (jq(so.elm).closest('.ihidden')[0]) return;
          // NOTE: DPO's own validateVariantConfig() also collects items where
          // `so.sku_is_product_id && so.separate_cart_item` into a linkedProducts
          // list and adds them as their own companion line items. Every option in
          // the current templates has separate_cart_item: 0, so omitting that
          // branch is a genuine no-op today. If a future template ever enables it,
          // those companion items would be silently dropped here — port DPO's
          // linkedProducts branch (and its post-add /cart/add.js calls) then.
          var name = (o.elm ? o.elm : so.elm).name.replace('[]', '');
          if (!options[name]) options[name] = [];
          options[name].push(so.elm.value);
        });
        return;
      }

      if (!o.elm || o.elm.disabled) return;
      var filename = jq(o.elm).attr('filename');
      if (o.hide_on_focus && o.elm.value == o.default_value && o.type !== 'slider') return;
      if (!o.elm.value && !filename) return;
      if (jq(o.elm).closest('.ihidden')[0]) return;
      options[o.elm.name] = filename ? filename : o.elm.value;
    });

    jq('.option-qty:not(.use_product_qty)').each(function () {
      options[this.name] = (this.value - 0 > 0) ? this.value - 0 : 1;
    });

    options.qty = qty;
    options.template_id = obs.forceLoadingTemplate;
    options.variant_id = jq(obs.selectors.variantId).val();
    options.product_id =
      (window.meta && window.meta.product && window.meta.product.id) || CFG.productId || 0;
    options.customer_id =
      (window.meta && window.meta.page && window.meta.page.customerId) || 0;
    options.tier_prices = JSON.stringify(dpo.tierPrices);
    options.price_handler = obs.priceHandler;
    options.cart_transform_relative_pricing = obs.cartTranformRelativePricing;
    if (obs.skipAssocDataCheck) options.skip_assoc_data_check = 1;

    // DPO wants to know which of its configurations are already in the cart.
    options.dpo_cart_items = (cartItems || []).reduce(function (acc, it) {
      var t = it.title || '';
      var p = t.indexOf('(D#');
      if (p > -1) acc.push({ dpo_cart_id: t.substr(p + 3, 7), qty: it.quantity || 1 });
      return acc;
    }, []);

    return options;
  }

  function fetchCartItems() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .then(function (c) { return (c && c.items) || []; })
      .catch(function () { return []; });
  }

  /** POST the config to DPO's ValidateForm controller. The server mints (or
   *  reuses) a real Shopify variant titled "… (D#XXXXXXX)" carrying the
   *  computed price, and returns its id. */
  function issueConfigVariant(payload) {
    return new Promise(function (resolve, reject) {
      window.jQuery
        .post(window.dpoObserver.formValidateUrl, payload)
        .done(function (data) {
          if (!data || data.error || data.notice || !data.variant_id) {
            var msg = (data && (data.error || data.notice)) ||
              'The product-options service could not build this configuration.';
            return reject(new Error(String(msg)));
          }
          resolve(data);
        })
        .fail(function () {
          reject(new Error('Could not reach the product-options service.'));
        });
    });
  }

  function liveFileInputs() {
    var root = document.getElementById(DPO_ROOT);
    if (!root) return [];
    return $$('input[type="file"]', root).filter(function (el) {
      return !el.disabled && !el.closest('.ihidden') && el.files && el.files.length;
    });
  }

  /** The line-item property name DPO would use for a file field. */
  function fileTitle(el) {
    if (el.config && el.config.title) return String(el.config.title);
    var wrap = el.closest('[id^="dynamic_option_id_"]');
    var label = wrap && wrap.querySelector('label');
    if (!label) return '';
    var clone = label.cloneNode(true);
    $$('em, span, a, input', clone).forEach(function (n) { n.remove(); });
    return (clone.textContent || '').trim();
  }

  /** Shopify rejects [ ] in property names; DPO swaps them for parens. */
  function propName(k) {
    return String(k).replace(/\[/g, '(').replace(/\]/g, ')');
  }

  /** Add ONE line item, carrying the full DPO property set plus any uploaded
   *  custom-logo artwork (multipart, exactly as DPO's own submit does). */
  function addLineItem(data, qty) {
    var props = {};
    try { props = window.dpoOptions.createShopifyCartProperties(true) || {}; } catch (e) { /* noop */ }

    var fd = new FormData();
    fd.append('id', data.variant_id);
    fd.append('quantity', qty);

    Object.keys(props).forEach(function (k) {
      fd.append('properties[' + propName(k) + ']', props[k] == null ? '' : props[k]);
    });

    if (data._dpo_id) {
      fd.append('properties[_dpo_id]', data._dpo_id);
      fd.append('properties[__dpo_hash]', data._dpo_hash || '');
      fd.append('properties[__dpo_p]', JSON.stringify(props));
    }
    if (data._dpo_v) fd.append('properties[_dpo_v]', data._dpo_v);

    liveFileInputs().forEach(function (el) {
      var title = fileTitle(el);
      if (!title) return;
      fd.append('properties[' + propName(title) + ']', el.files[0], el.files[0].name);
    });

    return fetch(CFG.routes.cartAdd, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        if (!res.ok) {
          throw new Error(body.description || body.message || ('Shopify rejected this ' + NOUN_LC + '.'));
        }
        return body;
      });
    });
  }

  /* ---------------------------------------------------------- */
  /* Rows                                                       */
  /* ---------------------------------------------------------- */

  function newRow(snap) {
    return {
      id: ++state.seq,
      snap: snap || cloneSnap(state.defaultSnap),
      qty: 1,
      price: null,
      summary: '',
      added: false,
      error: null
    };
  }

  /** DPO can silently blank a size dropdown at save/park time on the new
   *  multi-field Size Type structure. The size-pill proxy stamps the shopper's
   *  committed size on the ACTIVE size field as data-efx-size; before we
   *  snapshot, put that value back into the (possibly-emptied) select so the
   *  saved jersey keeps its size. Active-field-only (the proxy clears the stamp
   *  on inactive types), so it can never write another type's size. */
  function ensureSizesFilled() {
    var root = document.getElementById(DPO_ROOT);
    if (!root) return;
    $$('[id^="dynamic_option_id_"][data-efx-size]', root).forEach(function (field) {
      if (field.classList.contains('ihidden')) return; // inactive type — skip
      var want = field.getAttribute('data-efx-size');
      var sel = field.querySelector('select');
      if (!want || !sel || sel.value === want) return;
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].value === want) { sel.value = want; fire(sel); break; }
      }
    });
  }

  function captureActive() {
    if (state.active == null) return;
    var r = state.rows[state.active];
    if (!r || r.added) return;
    ensureSizesFilled(); // beat DPO's silent size-blank right before snapshotting
    r.snap = snapshot();
    r.price = currentPrice();
    r.summary = currentSummary();
  }

  function money(n) {
    if (n == null || isNaN(n)) return '—';
    return '$' + Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function rowTotal(r) {
    return r.price == null ? null : r.price * r.qty;
  }

  function grandTotal() {
    var sum = 0, known = true;
    state.rows.forEach(function (r) {
      var t = rowTotal(r);
      if (t == null) known = false;
      else sum += t;
    });
    return { sum: sum, known: known };
  }

  /* ---------------------------------------------------------- */
  /* Rendering                                                  */
  /* ---------------------------------------------------------- */

  var els = {};

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /** Move the live customizer into the parking bay so we can safely rebuild
   *  the row list without destroying it. */
  function park() {
    var cont = $(CONTAINER);
    if (cont && els.stage && cont.parentNode !== els.stage) els.stage.appendChild(cont);
  }

  function render() {
    park();

    els.rows.innerHTML = state.rows.map(function (r, i) {
      var isActive = state.active === i;
      var total = rowTotal(r);
      var cls = 'efx-to-row' +
        (isActive ? ' is-open' : '') +
        (r.added ? ' is-added' : '') +
        (r.error ? ' has-error' : '');

      return '' +
        '<div class="' + cls + '" data-idx="' + i + '">' +
          '<div class="efx-to-row__head" data-act="toggle" role="button" tabindex="0"' +
            ' aria-expanded="' + (isActive ? 'true' : 'false') + '">' +
            '<span class="efx-to-row__caret" aria-hidden="true"></span>' +
            '<span class="efx-to-row__label">' +
              '<span class="efx-to-row__n">' + NOUN + ' ' + (i + 1) + '</span>' +
              '<span class="efx-to-row__sum">' +
                (r.added
                  ? '<span class="efx-to-row__added">Added to cart</span>'
                  : esc(r.summary || 'No name')) +
              '</span>' +
            '</span>' +
            '<span class="efx-to-row__price">' + (total == null ? '' : money(total)) + '</span>' +
            '<span class="efx-to-row__qty" data-stop>' +
              '<button type="button" class="efx-to-q" data-act="qty-" aria-label="Decrease quantity"' +
                (r.added ? ' disabled' : '') + '>&minus;</button>' +
              '<span class="efx-to-q__n">' + r.qty + '</span>' +
              '<button type="button" class="efx-to-q" data-act="qty+" aria-label="Increase quantity"' +
                (r.added ? ' disabled' : '') + '>+</button>' +
            '</span>' +
            '<span class="efx-to-row__acts" data-stop>' +
              '<button type="button" class="efx-to-ic" data-act="dup" title="Duplicate this ' + NOUN_LC + '"' +
                ' aria-label="Duplicate ' + NOUN_LC + ' ' + (i + 1) + '">Duplicate</button>' +
              '<button type="button" class="efx-to-ic efx-to-ic--rm" data-act="rm"' +
                ' title="Remove this ' + NOUN_LC + '" aria-label="Remove ' + NOUN_LC + ' ' + (i + 1) + '">Remove</button>' +
            '</span>' +
          '</div>' +
          (r.error ? '<p class="efx-to-row__err">' + esc(r.error) + '</p>' : '') +
          '<div class="efx-to-row__body" id="efx-to-row-body-' + r.id + '">' +
            (isActive && !r.added
              ? '<div class="efx-to-row__done-wrap">' +
                  '<button type="button" class="efx-to-row__done" data-act="done">Save ' + NOUN_LC + '</button>' +
                '</div>'
              : '') +
          '</div>' +
        '</div>';
    }).join('');

    // Re-seat the one live customizer inside the row that's open.
    if (state.active != null) {
      var r = state.rows[state.active];
      var body = r && !r.added ? document.getElementById('efx-to-row-body-' + r.id) : null;
      var cont = $(CONTAINER);
      if (body && cont) body.insertBefore(cont, body.firstChild);
    }

    enforceFileSupport();
    renderFoot();
  }

  /** Fix 1b: on browsers without programmatic FileList support we cannot carry an
   *  uploaded file from one row to the next, so uploads are unsafe in the builder.
   *  While the customizer is INSIDE the drawer, disable the custom-logo file
   *  fields, mark their wrappers .field-disabled — which DPO's own dpoValidation
   *  treats as "skip", so a disabled-but-required file field can't block "Add
   *  all" — and drop an inline note pointing shoppers to the standard product
   *  page.
   *
   *  Critically, this REVERTS whenever the same shared customizer is back on the
   *  product page (drawer closed): the single-item flow uploads via DPO's native
   *  multipart submit, which needs no FileList reconstruction and must stay fully
   *  functional. Idempotent; a total no-op when FileList assignment is supported. */
  function enforceFileSupport() {
    if (canSetFiles) return;
    var root = document.getElementById(DPO_ROOT);
    if (!root) return;

    var inDrawer = !!(els.root && els.root.contains(root));

    $$('input[type="file"]', root).forEach(function (el) {
      var wrap = el.closest('[id^="dynamic_option_id_"]');

      if (inDrawer) {
        el.disabled = true;
        if (!wrap || wrap.classList.contains('efx-to-nofile')) return;
        wrap.classList.add('efx-to-nofile', 'field-disabled');
        var note = document.createElement('p');
        note.className = 'efx-to-nofile-note';
        note.textContent =
          "Custom logo uploads aren't supported in the team builder on this browser " +
          '— use the standard product page for logo uploads.';
        wrap.appendChild(note);
      } else {
        // Back on the product page — undo everything so the single-item flow works.
        el.disabled = false;
        if (!wrap || !wrap.classList.contains('efx-to-nofile')) return;
        wrap.classList.remove('efx-to-nofile', 'field-disabled');
        var stale = wrap.querySelector('.efx-to-nofile-note');
        if (stale) stale.remove();
      }
    });
  }

  function renderFoot() {
    var pending = state.rows.filter(function (r) { return !r.added; });
    var count = state.rows.reduce(function (n, r) { return n + r.qty; }, 0);
    var t = grandTotal();

    els.count.textContent = state.rows.length
      ? count + ' ' + nCount(count)
      : 'No ' + NOUN_PL + ' yet';

    renderDiscount(count, t);

    els.submit.disabled = state.busy || !pending.length;
    els.submitText.textContent = state.busy
      ? 'Adding…'
      : (pending.length && pending.length !== state.rows.length
          ? 'Add remaining ' + pending.length + ' to cart'
          : 'Add all to cart');

    els.addRow.disabled = state.busy || state.rows.length >= (CFG.maxRows || 24);
  }

  /** Show the projected bulk-discount estimate. This is DISPLAY ONLY — the
   *  storefront can't lower a line-item price, so the actual reduction is
   *  applied at checkout by the matching Shopify automatic discount the
   *  merchant configures. We surface the estimate and say so. */
  function renderDiscount(count, t) {
    var hasRows = state.rows.length > 0;
    var known = t.known;

    // Base total display (or em dash when nothing / price still resolving).
    els.total.textContent = hasRows ? (known ? money(t.sum) : '—') : '—';

    if (!TIERS.length || !hasRows || !known) {
      els.discount.hidden = true;
      els.totalWas.hidden = true;
      els.discountNote.hidden = !TIERS.length ? true : (hasRows ? false : true);
      if (!els.discountNote.hidden) nextTierNote(count);
      return;
    }

    var tier = tierFor(count);
    if (!tier) {
      els.discount.hidden = true;
      els.totalWas.hidden = true;
      nextTierNote(count);
      return;
    }

    var saved = t.sum * (tier.pct / 100);
    var net = t.sum - saved;

    els.totalWas.hidden = false;
    els.totalWas.textContent = money(t.sum);
    els.total.textContent = money(net);

    els.discount.hidden = false;
    els.discountLabel.textContent = 'Team discount (' + fmtPct(tier.pct) + '% off ' + count + '+)';
    els.discountAmt.textContent = '−' + money(saved);

    els.discountNote.hidden = false;
    els.discountNote.textContent = 'Estimate — applied at checkout via the store’s bulk discount.';
  }

  function nextTierNote(count) {
    if (!TIERS.length) { els.discountNote.hidden = true; return; }
    var next = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (count < TIERS[i].min) { next = TIERS[i]; break; }
    }
    els.discountNote.hidden = false;
    els.discountNote.textContent = next
      ? 'Add ' + (next.min - count) + ' more to unlock ' + fmtPct(next.pct) + '% off (' + next.min + '+).'
      : 'Bulk discount applied at checkout.';
  }

  function fmtPct(p) {
    return (p % 1 === 0) ? String(p) : p.toFixed(1);
  }

  function msg(text, kind) {
    els.msg.textContent = text || '';
    els.msg.className = 'efx-to__msg' + (text ? ' is-' + (kind || 'info') : '');
  }

  /* ---------------------------------------------------------- */
  /* Live header updates while editing                          */
  /* ---------------------------------------------------------- */

  /** Recompute ONLY the active row's header (summary + price) and the footer
   *  totals as the shopper edits — no full render(), which would re-seat the
   *  live customizer and interrupt them. Read-only against DPO: it just reads
   *  the computed price and option values. */
  function liveUpdateActiveRow() {
    if (state.busy || state.active == null) return;
    var r = state.rows[state.active];
    if (!r || r.added) return;

    // A Collar change can make DPO silently blank the size (no change event, so
    // the pill proxy's re-assert misses it) — refill it from the committed stamp
    // before we recompute, so the header doesn't intermittently drop the size.
    ensureSizesFilled();

    r.price = currentPrice();
    r.summary = currentSummary();

    var rowEl = els.rows.querySelector('.efx-to-row[data-idx="' + state.active + '"]');
    if (rowEl) {
      var sumEl = rowEl.querySelector('.efx-to-row__sum');
      if (sumEl) sumEl.innerHTML = esc(r.summary || 'No name');
      var priceEl = rowEl.querySelector('.efx-to-row__price');
      if (priceEl) { var t = rowTotal(r); priceEl.textContent = (t == null ? '' : money(t)); }
    }
    renderFoot(); // totals/discount only — doesn't touch the customizer
  }

  var liveTimer = null;
  /** Debounced: DPO fires a burst of change/input events per interaction (its
   *  cascade), so coalesce them into one recompute after things settle. */
  function scheduleLiveUpdate() {
    if (state.busy || state.active == null) return;
    if (liveTimer) clearTimeout(liveTimer);
    liveTimer = setTimeout(liveUpdateActiveRow, 220);
  }

  /* ---------------------------------------------------------- */
  /* Actions                                                    */
  /* ---------------------------------------------------------- */

  function openRow(i) {
    if (state.busy) return;
    var r = state.rows[i];
    if (!r || r.added) return;

    if (state.active === i) return collapse();
    if (state.active != null) captureActive();

    state.active = i;
    setThemeQty(r.qty);
    render();
    restore(r.snap);
    // Re-read price/summary once the rules have settled.
    setTimeout(function () {
      if (state.active !== i) return;
      r.price = currentPrice();
      r.summary = currentSummary();
      render();
    }, SETTLE_MS);
  }

  function collapse() {
    if (state.active == null) return;
    captureActive();
    state.active = null;
    render();
  }

  function addRow(snap) {
    if (state.rows.length >= (CFG.maxRows || 24)) return;
    if (state.active != null) captureActive();
    state.rows.push(newRow(snap));
    msg('');
    openRow(state.rows.length - 1);
  }

  function duplicateRow(i) {
    var src = state.rows[i];
    if (!src) return;

    // Flush whatever row is live first — including row i itself, so that
    // duplicating the row you're editing copies what's on screen, not a stale
    // snapshot.
    captureActive();

    var copy = newRow(cloneSnap(src.snap));
    copy.qty = src.qty;
    copy.price = src.price;
    copy.summary = src.summary;

    state.rows.splice(i + 1, 0, copy);

    // Leave the accordion closed after duplicating — don't auto-expand the copy.
    // captureActive() above already parked the live customizer and saved the
    // source row's edits, so collapsing everything is safe.
    state.active = null;
    park();
    msg('');
    render();
  }

  function removeRow(i) {
    if (state.busy) return;
    if (state.active === i) {
      park();               // rescue the live customizer before the row vanishes
      state.active = null;
    } else if (state.active != null && state.active > i) {
      state.active--;
    }
    state.rows.splice(i, 1);
    msg('');
    render();
  }

  /* ---------------------------------------------------------- */
  /* Made-to-order acknowledgement (mirrors the product page)   */
  /* ---------------------------------------------------------- */

  function pageMtoCheckbox() {
    var form = $(CFG.formSelector);
    return form ? form.querySelector('[data-mto-checkbox]') : null;
  }

  var mtoBound = false;

  function syncMto() {
    var page = pageMtoCheckbox();
    if (!page) return;

    els.mto.hidden = false;
    els.mtoCb.checked = page.checked;

    if (mtoBound) return;
    mtoBound = true;
    els.mtoCb.addEventListener('change', function () {
      var cb = pageMtoCheckbox();
      if (!cb) return;
      cb.checked = els.mtoCb.checked;
      fire(cb); // keeps the product page's own add-to-cart gate in sync
      msg('');
    });
  }

  function mtoBlocked() {
    var page = pageMtoCheckbox();
    return !!(page && !page.checked);
  }

  /* ---------------------------------------------------------- */
  /* Add all to cart                                            */
  /* ---------------------------------------------------------- */

  function setBusy(b) {
    state.busy = b;
    els.panel.classList.toggle('is-busy', b);
    renderFoot();
  }

  function addAll() {
    if (state.busy || !state.rows.length) return;

    if (mtoBlocked()) {
      msg('Please acknowledge the made-to-order notice below before adding to cart.', 'err');
      return;
    }

    captureActive();
    state.rows.forEach(function (r) { r.error = null; });

    var pending = state.rows.filter(function (r) { return !r.added; });
    if (!pending.length) return;

    setBusy(true);
    render(); // clear any stale error styling from a previous attempt
    msg('Checking your ' + NOUN_PL + '…', 'info');

    // --- Pass 1: validate EVERY pending row before adding ANY of them. ---
    // A validation failure should never leave a half-filled cart, and unlike a
    // network failure it is entirely knowable up front.
    preflight(pending, 0).then(function (bad) {
      if (bad) {
        var n = state.rows.indexOf(bad) + 1;
        bad.error = 'This ' + NOUN_LC + ' is missing required options.' + missingText(bad);
        state.active = state.rows.indexOf(bad);
        setBusy(false);
        setThemeQty(bad.qty);
        render();
        restore(bad.snap);
        setTimeout(function () {
          // Re-run so DPO paints its .dpo-error markers inside the now-visible row.
          try { window.dpoOptions.validateForm(); } catch (e) { /* noop */ }
          // Re-harvest now that the row is visible — a more complete marker set
          // may be painted here than during the headless preflight pass.
          var refined = invalidFieldNames();
          if (refined.length) {
            bad.missing = refined;
            bad.error = 'This ' + NOUN_LC + ' is missing required options.' + missingText(bad);
            render();
            msg(NOUN + ' ' + n + ' is missing required options.' + missingText(bad) +
                ' Nothing was added to your cart.', 'err');
          }
          scrollToFirstError();
        }, SETTLE_MS);
        msg(NOUN + ' ' + n + ' is missing required options.' + missingText(bad) +
            ' Nothing was added to your cart.', 'err');
        return;
      }
      return commit(pending, 0, []);
    }).catch(function (e) {
      setBusy(false);
      msg((e && e.message) || 'Something went wrong. Nothing further was added.', 'err');
      render();
    });
  }

  /** DPO's own scrollToValidationFailed() calls window.scrollTo(), but while the
   *  drawer is open <body> is overflow:hidden and the real scroll container is
   *  .efx-to__body — so its scroll goes nowhere and the offending field stays
   *  off-screen. Scroll the drawer instead. */
  function scrollToFirstError() {
    var err = els.rows.querySelector('.dpo-error, .validation-advice');
    if (!err) return;
    var field = err.closest('[id^="dynamic_option_id_"]') || err;
    try {
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch (e) {
      field.scrollIntoView();
    }
  }

  /** Names of the fields DPO just flagged as invalid. Read-only: harvests the
   *  .dpo-error / .validation-advice markers DPO paints during validateForm()
   *  and maps each back to its field's label, so the shopper is told WHICH
   *  options are missing instead of a bare "missing required options". Never
   *  moves or mutates DPO's DOM. */
  function invalidFieldNames() {
    var root = document.getElementById(DPO_ROOT);
    if (!root) return [];
    var names = [];
    $$('.dpo-error, .validation-advice', root).forEach(function (errEl) {
      var field = errEl.closest('[id^="dynamic_option_id_"]');
      if (!field) return;
      var lbl = field.querySelector('label');
      if (!lbl) return;
      var clone = lbl.cloneNode(true);
      $$('a, em, span, input', clone).forEach(function (n) { n.remove(); });
      var name = (clone.textContent || '').replace(/\s+/g, ' ').replace(/[:*\s]+$/, '').trim();
      if (name && names.indexOf(name) === -1) names.push(name);
    });
    return names;
  }

  /** Human list of missing options for a row's error text, e.g. "Size Type, Collar Style". */
  function missingText(row) {
    return (row.missing && row.missing.length) ? ' Missing: ' + row.missing.join(', ') + '.' : '';
  }

  /** Synchronously re-apply any select/text value the snapshot holds that has
   *  gone blank on the live customizer. DPO asynchronously blanks a dependent
   *  dropdown (notably a size, after a Size Type OR Collar change) shortly after
   *  restore() sets it; restore()'s timed re-fill can miss the exact instant we
   *  validate or build the add payload. Calling this immediately before those
   *  steps closes that race deterministically. Only fills BLANK fields from a
   *  non-empty saved value, so it can never override a real current selection. */
  function reassertFromSnap(snap) {
    if (!snap) return;
    controls().forEach(function (el) {
      if (el.type === 'file') return;
      var k = keyOf(el);
      if (!(k in snap.f)) return;
      var want = snap.f[k];
      if (el.type === 'radio' || el.type === 'checkbox') {
        // Restore the saved checked-state — notably Size Type, which is a
        // checkbox (options[1004][]) that DPO's cascade can uncheck mid-replay,
        // and which also cleans a type that bled in from another row. Safe here:
        // this runs only at preflight/commit, replaying a known-good snapshot,
        // never during live editing (where reverting a user's toggle would be
        // wrong — that's why restore()'s live refill still skips these).
        if (el.checked !== want) { el.checked = want; fire(el); }
      } else if (want && !el.value) {
        el.value = want; fire(el);
      }
    });
  }

  /** Resolves with the first invalid row, or null if all rows are good. */
  function preflight(pending, i) {
    if (i >= pending.length) return Promise.resolve(null);
    var r = pending[i];

    setThemeQty(r.qty);
    restore(r.snap);

    return settle().then(function () {
      reassertFromSnap(r.snap); // beat DPO's async blank right before validating
      var ok = false;
      try {
        ok = !!window.dpoOptions.customValidation() && !!window.dpoOptions.validateForm();
      } catch (e) {
        ok = false;
      }
      if (!ok) {
        // Capture WHICH fields failed now, while DPO's error markers are painted
        // for this row's restored config.
        r.missing = invalidFieldNames();
        return r;
      }

      r.missing = null;
      r.price = currentPrice();
      r.summary = currentSummary();
      return preflight(pending, i + 1);
    });
  }

  /** Adds rows one at a time. Each gets its own ValidateForm round trip and
   *  therefore its own (D#…) configuration variant. */
  function commit(pending, i, done) {
    if (i >= pending.length) {
      setBusy(false); // don't leave the footer stuck on "Adding…" if the nav stalls
      msg('Added ' + done.length + ' ' + nCount(done.length) + '. Taking you to your cart…', 'ok');
      window.location.href = CFG.routes.cart;
      return Promise.resolve();
    }

    var r = pending[i];
    var n = state.rows.indexOf(r) + 1;

    msg('Adding ' + NOUN_LC + ' ' + n + ' (' + (i + 1) + ' of ' + pending.length + ')…', 'info');

    setThemeQty(r.qty);
    restore(r.snap);

    return settle()
      .then(fetchCartItems)
      .then(function (cartItems) {
        reassertFromSnap(r.snap); // beat DPO's async blank right before building the payload
        return issueConfigVariant(buildValidatePayload(r.qty, cartItems));
      })
      .then(function (data) {
        return addLineItem(data, r.qty).then(function () {
          r.added = true;
          r.variantId = data.variant_id;
          done.push(n);
          render();
          return commit(pending, i + 1, done);
        });
      })
      .catch(function (e) {
        r.error = (e && e.message) || ('Could not add this ' + NOUN_LC + '.');
        setBusy(false);
        render();

        if (done.length) {
          msg(
            'Added ' + done.length + ' of ' + pending.length + ' ' + NOUN_PL + ' (' + NOUN_LC + ' ' +
            done.join(', ' + NOUN_LC + ' ') + ') — those are safely in your cart. ' + NOUN + ' ' + n +
            ' failed: ' + r.error + ' Press “Add remaining” to retry the rest.',
            'err'
          );
        } else {
          msg(
            NOUN + ' ' + n + ' could not be added: ' + r.error +
            ' Nothing was added to your cart.',
            'err'
          );
        }
      });
  }

  /* ---------------------------------------------------------- */
  /* Drawer open/close                                          */
  /* ---------------------------------------------------------- */

  function ensureHome() {
    if (state.home && state.home.isConnected) return;
    var cont = $(CONTAINER);
    if (!cont || !cont.parentNode) return;
    var a = document.createElement('div');
    a.id = 'efx-to-home';
    a.hidden = true;
    cont.parentNode.insertBefore(a, cont);
    state.home = a;
  }

  function open() {
    if (state.open || !dpoReady()) return;

    ensureHome();

    // Captured lazily, on first open, rather than the moment DPO reports ready:
    // several EFX phases (forced logos, sponsor sets, PBA bundles) write values
    // into DPO's controls on delays up to ~3.5s. By the time a shopper clicks
    // this button, those have settled, so this is the true baseline.
    if (!state.defaultSnap) state.defaultSnap = snapshot();

    state.pageSnap = snapshot();
    var q = themeQtyInput();
    state.pageQty = q ? q.value : null;

    state.open = true;
    els.root.hidden = false;
    document.body.classList.add('efx-to-locked');

    if (!state.rows.length) addRow();
    else render();

    syncMto();

    // Be honest rather than silently dropping artwork: without programmatic
    // FileList support we cannot carry an uploaded logo from one row to the next.
    if (!canSetFiles && document.querySelector('#' + DPO_ROOT + ' input[type="file"]')) {
      msg(
        'Heads up: this browser can’t carry uploaded logo files between ' + NOUN_PL + '. ' +
        'If you need custom uploaded artwork, add those ' + NOUN_PL + ' one at a time using the ' +
        'normal Add to Cart form instead.',
        'info'
      );
    } else {
      msg('');
    }

    els.panel.focus();
  }

  function close() {
    if (!state.open) return;
    if (state.busy) return;

    captureActive();
    state.active = null;

    // Put the live customizer back on the product page, exactly where DPO left
    // it, with exactly the answers the shopper had before they opened us.
    var cont = $(CONTAINER);
    if (cont && state.home && state.home.parentNode) {
      state.home.parentNode.insertBefore(cont, state.home);
    }
    if (state.pageQty != null) setThemeQty(state.pageQty);
    restore(state.pageSnap);

    // Now that the customizer is back on the product page, lift the file-field
    // lockdown so the untouched single-item flow can still upload logos.
    enforceFileSupport();

    state.open = false;
    els.root.hidden = true;
    document.body.classList.remove('efx-to-locked');
  }

  /* ---------------------------------------------------------- */
  /* Wiring                                                     */
  /* ---------------------------------------------------------- */

  function mountButton() {
    if (document.getElementById('efx-to-open')) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'efx-to-open';
    btn.className = 'efx-to-open';
    btn.innerHTML =
      '<span class="efx-to-open__text">Build a Team Order</span>' +
      '<span class="efx-to-open__hint">Customize several ' + NOUN_PL + ' and add them all at once</span>';
    btn.addEventListener('click', open);

    // Requested placement: directly above the configurator (i.e. above the
    // "Production Time Frame" field, which is the first field in .dpo-container).
    // DPO inserts .dpo-container just before .purchase-details, so placing the
    // button before the container puts it at the very top of the options.
    var cont = $(CONTAINER);
    if (cont && cont.parentNode) {
      cont.parentNode.insertBefore(btn, cont);
      return;
    }

    // Fallbacks if the container isn't found: just before the purchase details,
    // else next to Add to Cart (the original spot).
    var form = $(CFG.formSelector);
    var purchase = form && form.querySelector('.purchase-details');
    if (purchase && purchase.parentNode) {
      purchase.parentNode.insertBefore(btn, purchase);
      return;
    }
    var buttons = form && form.querySelector('.purchase-details__buttons');
    if (buttons) {
      var atc = buttons.querySelector('button[name="add"]');
      if (atc && atc.nextSibling) buttons.insertBefore(btn, atc.nextSibling);
      else buttons.appendChild(btn);
    }
  }

  function onRowsClick(e) {
    var actEl = e.target.closest('[data-act]');
    if (!actEl || !els.rows.contains(actEl)) return;

    var rowEl = actEl.closest('.efx-to-row');
    if (!rowEl) return;
    var i = parseInt(rowEl.getAttribute('data-idx'), 10);
    var act = actEl.getAttribute('data-act');

    if (act === 'toggle') {
      // Don't toggle when the click came from the qty/dup/remove cluster.
      if (e.target.closest('[data-stop]')) return;
      return openRow(i);
    }

    e.preventDefault();
    e.stopPropagation();

    if (act === 'done') return collapse();
    if (act === 'dup') return duplicateRow(i);
    if (act === 'rm') return removeRow(i);

    if (act === 'qty+' || act === 'qty-') {
      var r = state.rows[i];
      if (!r || r.added) return;
      var next = r.qty + (act === 'qty+' ? 1 : -1);
      r.qty = Math.max(1, Math.min(99, next));
      if (state.active === i) setThemeQty(r.qty);
      render();
    }
  }

  function onRowsKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var head = e.target.closest('.efx-to-row__head');
    if (!head) return;
    if (e.target.closest('[data-stop]')) return;
    e.preventDefault();
    var rowEl = head.closest('.efx-to-row');
    openRow(parseInt(rowEl.getAttribute('data-idx'), 10));
  }

  var poll = null;

  /** Runs on first load, and again whenever the theme editor swaps the product
   *  section out from under us (which replaces the drawer's DOM and would
   *  otherwise leave every els.* reference pointing at detached nodes). */
  function boot() {
    var root = document.getElementById('efx-to');
    if (!root || root === els.root) return; // nothing new to bind

    // Fresh DOM means the old rows referenced a customizer that no longer exists.
    if (poll) clearInterval(poll);
    document.body.classList.remove('efx-to-locked');
    state.rows = [];
    state.active = null;
    state.seq = 0;
    state.defaultSnap = null;
    state.pageSnap = null;
    state.pageQty = null;
    state.home = null;
    state.open = false;
    state.busy = false;
    mtoBound = false;

    init();
  }

  function init() {
    els.root = document.getElementById('efx-to');
    if (!els.root) return;

    els.panel = $('.efx-to__panel', els.root);
    els.rows = document.getElementById('efx-to-rows');
    els.stage = document.getElementById('efx-to-stage');
    els.addRow = document.getElementById('efx-to-add-row');
    els.submit = document.getElementById('efx-to-submit');
    els.submitText = document.getElementById('efx-to-submit-text');
    els.count = document.getElementById('efx-to-count');
    els.total = document.getElementById('efx-to-total');
    els.totalWas = document.getElementById('efx-to-total-was');
    els.discount = document.getElementById('efx-to-discount');
    els.discountLabel = document.getElementById('efx-to-discount-label');
    els.discountAmt = document.getElementById('efx-to-discount-amt');
    els.discountNote = document.getElementById('efx-to-discount-note');
    els.msg = document.getElementById('efx-to-msg');
    els.mto = document.getElementById('efx-to-mto');
    els.mtoCb = document.getElementById('efx-to-mto-cb');

    els.panel.setAttribute('tabindex', '-1');

    // Build string is kept for the console log only (internal version marker);
    // it is intentionally NOT shown in the drawer UI in production.

    els.rows.addEventListener('click', onRowsClick);
    els.rows.addEventListener('keydown', onRowsKeydown);
    els.addRow.addEventListener('click', function () { addRow(); });
    els.submit.addEventListener('click', addAll);

    // Live header/price updates while a jersey is being edited. Capture-phase so
    // we see every DPO change; filtered to the live customizer's own inputs.
    els.rows.addEventListener('change', function (e) {
      if (e.target.closest && e.target.closest(CONTAINER)) scheduleLiveUpdate();
    }, true);
    els.rows.addEventListener('input', function (e) {
      if (e.target.closest && e.target.closest(CONTAINER)) scheduleLiveUpdate();
    }, true);

    $$('[data-efx-to-close]', els.root).forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.open) close();
    });

    // The button is only meaningful once DPO has rendered its options, so wait.
    var tries = 0;
    poll = setInterval(function () {
      if (dpoReady()) {
        clearInterval(poll);
        poll = null;
        mountButton();
      } else if (++tries > 120) {
        clearInterval(poll); // DPO never showed up (60s) — stay out of the way
        poll = null;
      }
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
